/**
 * 景點編輯表單的狀態與純函式。
 *
 * 從 `SpotEdit.tsx` 抽出來：該頁改成分頁後，表單狀態同時被四個分頁與頁尾的儲存
 * 按鈕使用，而清洗規則（哪些算「空」）本身與畫面無關，也是這頁最值得單獨看懂的
 * 部分。
 */

import { richTextIsEmpty, sanitizeRichText } from './RichTextEditor';
import {
  displayLocaleHasContent,
  emptyDisplayForm,
  type DisplayFormState,
} from './SpotDisplayFields';
import type { Spot } from '../lib/api-client';

/**
 * 單行／多行輸入的舒適上限（px）。
 *
 * 景點編輯的容器跟著頁面寬度走（`SpotEdit` 沒有固定 maxWidth），因為推薦點卡片
 * 與產出預覽真的吃寬度。但一個「景點代碼」欄位在 2560px 螢幕上拉成 2400px 寬並
 * 不會比較好填——游標離標籤太遠，也看不出該填多長。
 *
 * 三個檔共用同一個值（basic / display / poi 的欄位包裝），各寫各的遲早會對不齊。
 */
export const FIELD_MAX_WIDTH = 720;

export const LOCALES = ['zh-TW', 'en', 'ja'] as const;
export type Locale = (typeof LOCALES)[number];
export const LOCALE_LABEL: Record<Locale, string> = { 'zh-TW': '繁中', en: 'EN', ja: '日本語' };

export interface SpotFormState {
  spot_code: string;
  name: Record<Locale, string>;
  description: Record<Locale, string>; // 各語言為 HTML（RichTextEditor 輸出）
  latitude: string;
  longitude: string;
  trigger_radius_m: string;
  /** 窗屏景點編號；空字串 = 此景點不納入窗屏導覽 */
  roi_id: string;
  display: DisplayFormState;
}

export function emptySpotForm(): SpotFormState {
  return {
    spot_code: '',
    name: { 'zh-TW': '', en: '', ja: '' },
    description: { 'zh-TW': '', en: '', ja: '' },
    latitude: '',
    longitude: '',
    trigger_radius_m: '100',
    roi_id: '',
    display: emptyDisplayForm(),
  };
}

/** 名稱（純文字）：去空字串，全空 → undefined。 */
export function cleanNameI18n(rec: Record<Locale, string>): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const l of LOCALES) {
    const v = rec[l].trim();
    if (v) out[l] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** 描述（HTML）：去「視覺為空」者，留存前再淨化一次（防直接寫入之惡意 HTML）。 */
export function cleanDescriptionI18n(
  rec: Record<Locale, string>,
): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const l of LOCALES) {
    const html = rec[l];
    if (!richTextIsEmpty(html)) out[l] = sanitizeRichText(html);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** 是否含內容（給語言頁籤的小圓點提示）。 */
export function localeHasContent(form: SpotFormState, l: Locale): boolean {
  return (
    form.name[l].trim().length > 0 ||
    !richTextIsEmpty(form.description[l]) ||
    displayLocaleHasContent(form.display, l)
  );
}

/** API 回應 → 表單狀態。 */
export function spotToForm(s: Spot, display: DisplayFormState): SpotFormState {
  return {
    spot_code: s.spot_code,
    name: {
      'zh-TW': s.name_i18n['zh-TW'] ?? '',
      en: s.name_i18n.en ?? '',
      ja: s.name_i18n.ja ?? '',
    },
    description: {
      'zh-TW': s.description_i18n?.['zh-TW'] ?? '',
      en: s.description_i18n?.en ?? '',
      ja: s.description_i18n?.ja ?? '',
    },
    latitude: String(s.latitude),
    longitude: String(s.longitude),
    trigger_radius_m: String(s.trigger_radius_m),
    roi_id: s.roi_id === null ? '' : String(s.roi_id),
    display,
  };
}

/**
 * 送出前的欄位檢查，回第一個錯誤訊息（null = 通過）。
 *
 * 回訊息而非丟例外：呼叫端要把它顯示在表單上方，而不是當成非預期錯誤處理。
 */
export function validateSpotForm(form: SpotFormState, isEdit: boolean): string | null {
  if (!cleanNameI18n(form.name)) return '名稱至少需填一種語言';

  const lat = Number(form.latitude);
  const lng = Number(form.longitude);
  if (Number.isNaN(lat) || lat < -90 || lat > 90) return '緯度需為 -90~90';
  if (Number.isNaN(lng) || lng < -180 || lng > 180) return '經度需為 -180~180';

  const radius = Number(form.trigger_radius_m);
  if (Number.isNaN(radius) || radius < 1 || radius > 10_000) return '觸發半徑需為 1~10000 公尺';

  const roiRaw = form.roi_id.trim();
  if (roiRaw !== '') {
    const roiId = Number(roiRaw);
    if (!Number.isInteger(roiId) || roiId < 0) return '窗屏景點編號需為 0 以上的整數';
  }

  if (!isEdit && form.spot_code.trim().length < 2) return 'spot_code 至少 2 字';
  return null;
}
